import { useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { FileError } from './FileError';
import { validateFile } from '../lib/fileValidation';

// Resizes an image File to a square base64 JPEG via canvas (cover-fit, centred).
// Used for both circular people photos and square org logos. Default 200px.
function resizeToBase64(file, size = 200, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Image decode failed'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2d context not available'));
          return;
        }
        // cover-fit: scale so the shorter dimension covers the canvas.
        const scale = Math.max(size / img.width, size / img.height);
        const sw = img.width * scale;
        const sh = img.height * scale;
        const sx = (size - sw) / 2;
        const sy = (size - sh) / 2;
        ctx.drawImage(img, sx, sy, sw, sh);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// PhotoUpload — click to pick, validate against the 'asset' category (image-only,
// 5MB cap), resize to 200x200 base64, preview in shape.
// Props:
//   value: base64 string or null
//   onChange: (newValue) => void  (newValue is base64 string or null)
//   shape: 'circle' | 'square'  (default 'circle')
//   size: pixel size (default 80)
export function PhotoUpload({ value, onChange, shape = 'circle', size = 80, label }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError(null);
    const result = validateFile(file, 'asset');
    if (!result.valid) {
      setError({ key: result.errorKey, params: result.params });
      return;
    }

    try {
      const dataUrl = await resizeToBase64(file, 200, 0.85);
      onChange(dataUrl);
    } catch (err) {
      console.error('[photo-upload] resize failed:', err);
      setError({ key: 'upload.errors.fileCorrupt', params: {} });
    }
  }

  function handleRemove(e) {
    e.stopPropagation();
    setError(null);
    onChange(null);
  }

  const placeholder = label || (shape === 'square' ? t('photoUpload.addLogo') : t('photoUpload.addPhoto'));
  const changeLabel = shape === 'square' ? t('photoUpload.changeLogo') : t('photoUpload.changePhoto');

  return (
    <div className="photo-upload-wrap">
      <div
        className={`photo-upload photo-upload--${shape}`}
        style={{ width: size, height: size }}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label={value ? changeLabel : placeholder}
      >
        {value ? (
          <>
            <img className="photo-upload__preview" src={value} alt="" />
            <button
              type="button"
              className="photo-upload__remove"
              onClick={handleRemove}
              aria-label={t('photoUpload.removePhoto')}
            >
              ×
            </button>
            <span className="photo-upload__overlay">{changeLabel}</span>
          </>
        ) : (
          <span>+ {placeholder}</span>
        )}
        <input
          ref={inputRef}
          className="photo-upload__input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/gif"
          onChange={handleFile}
        />
      </div>

      {error && <FileError errorKey={error.key} params={error.params} />}
    </div>
  );
}
